import { useMemo } from 'react';
import { useAppSelector } from '@store/hooks';
import {
  selectAlertsByFloorId,
  selectEquipmentByFloorId,
  selectFloorChips,
  selectFloors,
  selectHoistsByFixedFloorId,
  selectManagersByFloorId,
  selectProfessions,
  selectSelectedProfessionIds,
  selectUtilitiesByFloorId,
  type FloorWithChips,
} from '@store/selectors/monitor.selectors';
import { useLayerChips, type ILayerChips } from '../layers/useLayerData';
import type { IReplayView } from '../replay/buildReplayView';
import { useReplayViewContext } from '../replay/ReplayViewContext';
import { buildTradesByFloorId, tradesFromChips, type FloorTradeRow } from './floorTrades';

type AlertsByFloor = ReturnType<typeof selectAlertsByFloorId>;
type HoistsByFixedFloor = ReturnType<typeof selectHoistsByFixedFloorId>;

/** What the building draws: the live data, or the replay frame at the playhead. */
export interface IBuildingSource extends Pick<
  IReplayView,
  'chipsByFloor' | 'equipmentByFloor' | 'utilitiesByFloor' | 'managersByFloor'
> {
  /** The replay view at the playhead (for its event layer); null live and on the Dashboard. */
  replay: IReplayView | null;
  /** The popover's per-floor trade rows (live, with tag lists; replayed, the frame's trades without them). */
  tradesByFloorId: ReadonlyMap<string, FloorTradeRow[]>;
  /** The Activities / Issues layer chips (live only). */
  layerChips: ILayerChips | null;
  /** The alert badges (live only). */
  alertsByFloor: AlertsByFloor;
  /** The hoist badges (live only). */
  hoistsByFixedFloor: HoistsByFixedFloor;
  /** Drives the chip glide: the replay frame's key; null live. */
  glideFrameKey: string | null;
  /** A chip's glide key while replaying; undefined live (no glide). */
  glideKey(key: string): string | undefined;
}

const NO_TRADES: ReadonlyMap<string, FloorTradeRow[]> = new Map();
const NO_ALERTS: AlertsByFloor = new Map();
const NO_HOISTS: HoistsByFixedFloor = new Map();
const liveGlideKey = () => undefined;
const replayGlideKey = (key: string) => key;

/**
 * Resolves the building's inputs once: the live selectors, or — on the Monitor
 * while a day is replayed — the frame at the playhead, with the live-only
 * overlays (alert and hoist badges, the Activities / Issues layer chips, the
 * popover's tag lists) emptied over a past minute; its trade rows come from the frame.
 */
export function useBuildingSource(): IBuildingSource {
  const floorChips = useAppSelector(selectFloorChips);
  const floors = useAppSelector(selectFloors);
  const professions = useAppSelector(selectProfessions);
  const selectedProfessionIds = useAppSelector(selectSelectedProfessionIds);
  const equipmentByFloor = useAppSelector(selectEquipmentByFloorId);
  const utilitiesByFloor = useAppSelector(selectUtilitiesByFloorId);
  const managersByFloor = useAppSelector(selectManagersByFloorId);
  const alertsByFloor = useAppSelector(selectAlertsByFloorId);
  const hoistsByFixedFloor = useAppSelector(selectHoistsByFixedFloorId);
  const layerChips = useLayerChips();
  const replay = useReplayViewContext();
  const chipsByFloor = useMemo(() => new Map<string, FloorWithChips>(floorChips.map((floor) => [floor.floorId, floor])), [floorChips]);
  // Per-floor trade rows (label, color, list of tag ids) — see
  // `buildTradesByFloorId` for why this comes from the raw `floors` payload.
  const tradesByFloorId = useMemo(
    () => buildTradesByFloorId(floors, professions, selectedProfessionIds),
    [floors, professions, selectedProfessionIds],
  );
  // Replayed, the popover's trade rows come from the frame's own chips.
  const replayChips = replay?.chipsByFloor;
  const replayTrades = useMemo(() => (replayChips ? tradesFromChips(replayChips) : NO_TRADES), [replayChips]);
  if (replay) {
    return {
      replay,
      chipsByFloor: replay.chipsByFloor,
      equipmentByFloor: replay.equipmentByFloor,
      utilitiesByFloor: replay.utilitiesByFloor,
      managersByFloor: replay.managersByFloor,
      tradesByFloorId: replayTrades,
      layerChips: null,
      alertsByFloor: NO_ALERTS,
      hoistsByFixedFloor: NO_HOISTS,
      glideFrameKey: replay.frameKey,
      glideKey: replayGlideKey,
    };
  }
  return {
    replay,
    chipsByFloor,
    equipmentByFloor,
    utilitiesByFloor,
    managersByFloor,
    tradesByFloorId,
    layerChips,
    alertsByFloor,
    hoistsByFixedFloor,
    glideFrameKey: null,
    glideKey: liveGlideKey,
  };
}
